import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { RadioButton, RadioButtonProps, Stack } from '@shopify/polaris';
import Input, { InputContainer, InputProps } from '@ui/input';
import styled from 'styled-components';
import { FeatureKey } from '@givz/core-client';

export interface ExtendedRadioButtonProps extends RadioButtonProps {
  inlineInput?: Partial<InputProps>;
  feature?: FeatureKey;
}

export interface RadioStackProps {
  disabled?: boolean;
  initialState: string;
  onToggle?: (status: boolean, id: string) => void;
  onInputChange?(value: string, id: string): void;
  buttons: ExtendedRadioButtonProps[];
}

const NestedInputContainer = styled(InputContainer)`
  margin: 0 0 1.1rem 2.8rem;
`;

const RadioStack: FunctionComponent<RadioStackProps> = ({
  buttons,
  disabled,
  initialState,
  onInputChange,
  onToggle,
}) => {
  const [value, setValue] = useState(initialState);

  const handleChange = useCallback(
    (_checked, newValue) => {
      setValue(newValue);
      if (onToggle) {
        onToggle(_checked, newValue);
      }
    },
    [onToggle],
  );

  useEffect(() => {
    setValue(initialState);
  }, [initialState]);

  return (
    <Stack vertical spacing="extraTight">
      {buttons.map(({ id, ...props }: ExtendedRadioButtonProps) => (
        <div key={id}>
          <RadioButton
            {...props}
            disabled={props.disabled ?? disabled}
            id={id}
            checked={value === id}
            onChange={handleChange}
          />
          {props.inlineInput && onInputChange && value === id && (
            <NestedInputContainer>
              <Input
                id={id}
                {...(props.inlineInput as InputProps)}
                onChange={onInputChange}
              />
            </NestedInputContainer>
          )}
        </div>
      ))}
    </Stack>
  );
};

export default RadioStack;
